import * as React from 'react'
import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { ChevronLeft } from 'lucide-react'
import type { Resident } from './api'
import { listVitals, listFaxJobsForResident } from '@/features/vitals/api'
import { formatPhoneDisplay } from '@/lib/phone'
import { VitalsForm } from '@/features/vitals/VitalsForm'
import { VitalsTimeline } from '@/features/vitals/VitalsTimeline'
import { AddResidentDrawer } from './AddResidentDrawer'
import { Avatar } from '@/components/primitives/Avatar'
import './ResidentDetail.css'

interface Props {
  resident: Resident
  onBack?: () => void
}

function calcAge(dob: string): number | null {
  const birth = new Date(dob)
  if (isNaN(birth.getTime())) return null
  const today = new Date()
  let age = today.getFullYear() - birth.getFullYear()
  const m = today.getMonth() - birth.getMonth()
  if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--
  return age
}

export function ResidentDetail({ resident, onBack }: Props): React.JSX.Element {
  const [editOpen, setEditOpen] = useState(false)

  const { data: vitals, isLoading, isError: vitalsError } = useQuery({
    queryKey: ['vitals', resident.id],
    queryFn: () => listVitals(resident.id),
    staleTime: 10_000,
  })

  const { data: faxJobs } = useQuery({
    queryKey: ['fax-jobs', 'resident', resident.id],
    queryFn: () => listFaxJobsForResident(resident.id),
    staleTime: 30_000,
  })

  const last = vitals?.[0]?.taken_at
  const age = resident.dob ? calcAge(resident.dob) : null

  return (
    <section className="resident-detail">
      {/* ── Mobile back button ───────────────────────────────────────── */}
      {onBack != null && (
        <button type="button" className="resident-detail-back" onClick={onBack}>
          <ChevronLeft size={16} strokeWidth={2} />
          Residents
        </button>
      )}
      {/* ── Full-width resident header ───────────────────────────────── */}
      <header className="resident-header">
        <div className="resident-header-main">
          <Avatar name={`${resident.first_name} ${resident.last_name}`} size="lg" gradient />
          <div>
            <div className="resident-name-row">
              <h1 className="resident-name">
                {resident.first_name} {resident.last_name}
              </h1>
              <button type="button" className="resident-edit-btn" onClick={() => setEditOpen(true)} title="Edit resident">
                <PencilIcon />
                Edit
              </button>
            </div>
            <div className="resident-meta-row">
              {resident.mrn ? (
                <span className="resident-meta-item">
                  <span className="resident-meta-key">MRN</span>
                  <span className="resident-meta-val">{resident.mrn}</span>
                </span>
              ) : null}
              {resident.dob ? (
                <span className="resident-meta-item">
                  <span className="resident-meta-key">DOB</span>
                  <span className="resident-meta-val">{resident.dob}</span>
                </span>
              ) : null}
              {age !== null ? (
                <span className="resident-meta-item">
                  <span className="resident-meta-key">Age</span>
                  <span className="resident-meta-val">{age} yrs</span>
                </span>
              ) : null}
              {resident.sex ? (
                <span className="resident-meta-item">
                  <span className="resident-meta-key">Sex</span>
                  <span className="resident-meta-val">{resident.sex.charAt(0).toUpperCase() + resident.sex.slice(1)}</span>
                </span>
              ) : null}
              {resident.pcp_name ? (
                <span className="resident-meta-item">
                  <span className="resident-meta-key">PCP</span>
                  <span className="resident-meta-val">
                    {resident.pcp_name}{resident.pcp_fax ? ` · ${resident.pcp_fax}` : ''}
                  </span>
                </span>
              ) : null}
              {resident.phone ? (
                <span className="resident-meta-item">
                  <span className="resident-meta-key">Phone</span>
                  <span className="resident-meta-val">{formatPhoneDisplay(resident.phone)}</span>
                </span>
              ) : null}
            </div>
          </div>
        </div>
      </header>

      {/* ── Two-column grid: medical+vitals left, history right ──────── */}
      <div className="resident-layout">
        <div className="resident-main">
          {/* Medical info */}
          <div className="medical-info-section">
            <div className="section-eyebrow-row">
              <InfoIcon />
              <span className="section-eyebrow-text">MEDICAL INFO</span>
            </div>
            <div className="medical-info-grid">
              <div className="medical-info-col">
                <div className="medical-col-title">PROBLEMS</div>
                <p className="medical-empty muted small">No problems on record.</p>
              </div>
              <div className="medical-info-col">
                <div className="medical-col-title">MEDICATIONS</div>
                <p className="medical-empty muted small">No medications on record.</p>
              </div>
            </div>
          </div>

          {/* Vitals form — keyed by resident so the draft never carries over */}
          <VitalsForm key={resident.id} resident={resident} lastTakenAt={last} />
        </div>

        {/* ── Right column — History ───────────────────────────────────── */}
        <aside className="resident-history">
          <div className="history-panel-head">
            <ClockIcon />
            <span>Vitals history</span>
          </div>
          <div className="history-panel-body">
            {vitalsError ? (
              <p className="resident-detail-error">Failed to load vitals history. Please refresh.</p>
            ) : (
              <VitalsTimeline vitals={vitals ?? []} loading={isLoading} faxJobs={faxJobs} />
            )}
          </div>
        </aside>
      </div>

      <AddResidentDrawer
        open={editOpen}
        onClose={() => setEditOpen(false)}
        resident={resident}
        facilityId={resident.facility_id}
      />
    </section>
  )
}

function PencilIcon(): React.JSX.Element {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
    </svg>
  )
}

function InfoIcon(): React.JSX.Element {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="8" x2="12" y2="12" />
      <line x1="12" y1="16" x2="12.01" y2="16" />
    </svg>
  )
}

function ClockIcon(): React.JSX.Element {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </svg>
  )
}

